import { Nullable } from '../../types/common'
import { checkMobileVersion } from '../../utils/check-media'
import Sidebar from '../sidebar/sidebar'

class SearchSidebar extends Sidebar {
  searchInputSelector: string
  searchInputComponentSelector: string
  contentSelector: string
  contentResultsSelector: string
  content: Nullable<HTMLElement>
  contentResults: Nullable<HTMLElement>
  searchInputComponent: Nullable<HTMLElement>
  searchInput: Nullable<HTMLInputElement>
  resizeObserver: ResizeObserver
  isInputFocused: boolean
  defaultStateElement: Nullable<HTMLElement>

  constructor() {
    super()

    this.searchInputSelector = `[data-sidebar-search-input]`
    this.searchInputComponentSelector = `[data-sidebar-search-input-component]`
    this.contentSelector = `[data-sidebar-search-content]`
    this.contentResultsSelector = `[data-sidebar-search-content-results]`

    this.content = this.element.querySelector(this.contentSelector)
    this.contentResults = this.element.querySelector(
      this.contentResultsSelector,
    )

    this.searchInputComponent = this.element.querySelector(
      this.searchInputComponentSelector,
    )
    this.searchInput = this.element.querySelector(this.searchInputSelector)

    this.resizeObserver = new ResizeObserver(this.handleContentResultsResize)
    if (this.contentResults) {
      this.resizeObserver.observe(this.contentResults)
    }

    this.defaultStateElement = this.element.cloneNode(true) as HTMLElement
  }

  connectedCallback(): void {
    super.connectedCallback()

    if (this.content) {
      this.content.addEventListener('scroll', this.handleContentScroll)
    }

    this.addEventListener('click', this.handleClick)
    this.addEventListener('touchend', this.handleSearchSidebarTouchEnd)

    if (this.searchInput) {
      this.searchInput.addEventListener('focus', this.handleInputFocus)
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()

    if (this.content) {
      this.content.removeEventListener('scroll', this.handleContentScroll)
    }

    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('touchend', this.handleSearchSidebarTouchEnd)

    if (this.searchInput) {
      this.searchInput.removeEventListener('focus', this.handleInputFocus)
    }

    this.resizeObserver.disconnect()
  }

  handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    const isMobile = checkMobileVersion()
    const isInput = target === this.searchInput
    const isLink = target.tagName === 'A'
    const queryElement = target.closest('[data-sidebar-search-query]')

    if (this.searchInput && queryElement) {
      const event = new Event('input', { bubbles: true })

      this.searchInput.value = `${queryElement.getAttribute(
        'data-sidebar-search-query',
      )}`

      this.searchInput.dispatchEvent(event)

      this.searchInput.focus()
    }

    if (isMobile && isInput) {
      this.isInputFocused = true
    }

    if (!isInput && this.isInputFocused) {
      if (isLink) {
        event.preventDefault()
      }

      this.isInputFocused = false
    }
  }

  handleInputFocus = (): void => {
    const isMobile = checkMobileVersion()

    if (isMobile) {
      this.isInputFocused = true
    }
  }

  handleSearchSidebarTouchEnd = (event: TouchEvent): void => {
    const THRESHOLD = 12

    const target = event.target as HTMLElement
    const distanceX = Math.abs(this.touchStartX - this.touchEndX)
    const distanceY = Math.abs(this.touchStartY - this.touchEndY)
    const isDistanceOverThreshold =
      distanceX > THRESHOLD || distanceY > THRESHOLD
    const isLink = target.tagName === 'A'

    if (this.isInputFocused && isDistanceOverThreshold && this.searchInput) {
      if (isLink) {
        event.preventDefault()
      }

      this.searchInput.blur()

      this.isInputFocused = false
    }

    this.cleanTouchPoints()
  }

  handleContentResultsResize = (): void => {
    this.updateViewport()
  }

  handleContentScroll = (): void => {
    if (this.content) {
      const isContentOverflow = this.checkContentOverflow()
      const isScrolled = this.content.scrollTop > 0

      this.toggleAttribute(
        'header-shadow-visible',
        isContentOverflow && isScrolled,
      )
    }
  }

  updateViewport(): void {
    const isContentOverflow = this.checkContentOverflow()

    this.toggleAttribute('footer-shadow-visible', isContentOverflow)
  }

  open(): any {
    return super.open().then(() => {
      if (this.searchInput && !this.isEditorMode) {
        this.searchInput.focus()
      }
    })
  }

  checkContentOverflow(): boolean {
    if (!this.contentResults || !this.content) {
      return false
    }

    return this.contentResults.offsetHeight > this.content.offsetHeight
  }
}

export default SearchSidebar
